import { reactive, ref } from 'vue'

import type { Notification, NotificationData, NotificationType } from '../types'

export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info',
  LOADING: 'loading',
} as const satisfies Record<string, NotificationType>

const DEFAULT_DURATION = 2500

const notifications = ref<Notification[]>([])

let idSeq = 0

const isPersistent = (notification: Notification): boolean => notification.type === NOTIFICATION_TYPES.LOADING && !notification.time

function pushNotification(data: NotificationData): number {
  const notification: Notification = {
    ...data,
    position: data.position || 'bottom-center',
    id: ++idSeq,
  }

  if (!isPersistent(notification)) {
    notification.timerId = setTimeout(() => {
      notifications.value = notifications.value.filter((n) => n.id !== notification.id)
    }, notification.time || DEFAULT_DURATION)
  }

  notifications.value.push(notification)

  return notification.id
}

function clearTimer(id: number): void {
  const notification = notifications.value.find((n) => n.id === id)
  if (!notification) return

  clearTimeout(notification.timerId ?? undefined)
  notification.timerId = null
}

function startTimer(id: number): void {
  const notification = notifications.value.find((n) => n.id === id)
  if (!notification || isPersistent(notification)) return

  notification.timerId = setTimeout(() => {
    notifications.value = notifications.value.filter((n) => n.id !== id)
  }, notification.time || DEFAULT_DURATION)
}

function removeItem(id: number): void {
  const index = notifications.value.findIndex((n) => n.id === id)
  if (index !== -1) {
    clearTimeout(notifications.value[index].timerId ?? undefined)
    notifications.value.splice(index, 1)
  }
}

function clearTransient(): void {
  const isTransient = (n: Notification): boolean =>
    n.type === NOTIFICATION_TYPES.SUCCESS || n.type === NOTIFICATION_TYPES.INFO
  notifications.value.filter(isTransient).forEach((n) => clearTimeout(n.timerId ?? undefined))
  notifications.value = notifications.value.filter((n) => !isTransient(n))
}

const store = reactive({
  notifications,
  pushNotification,
  clearTimer,
  startTimer,
  removeItem,
  clearTransient,
})

export function useNotificationStore() {
  return store
}
